import React, { useState } from 'react';
import { 
  Activity, 
  Database, 
  Users, 
  Calendar,
  Eye
} from 'lucide-react';
import { SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import DatePicker from '../../components/client/DatePicker';
import SystemLogDetailsModal from '../../components/admin/SystemLogDetailsModal';
import { useAdminSystemLogs } from '../../hooks/useAdminSystemLogs';
import { formatDate, formatTime } from '../../utils/dateUtils';

const AdminSystemLogs = () => {
  const [selectedLog, setSelectedLog] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const {
    systemLogs,
    isLoading,
    error,
    filters,
    logTypeOptions,
    handleFilterChange,
    handleSearch,
    getLogTypeColor,
    statistics: stats
  } = useAdminSystemLogs();

  // Define table columns
  const columns = [
    {
      key: 'log_type',
      label: 'Type',
      sortable: true,
      width: 'w-32',
      render: (log) => {
        const color = getLogTypeColor(log.log_type);
        return (
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${color}`}>
            {log.log_type === 'admin_action' ? 'Admin Action' : 'User Activity'}
          </span>
        );
      }
    },
    {
      key: 'user',
      label: 'User/Admin',
      sortable: true,
      width: 'w-48',
      render: (log) => {
        const user = log.user || log.admin;
        return (
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user?.name || 'System'}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {user?.email || ''}
            </p>
          </div>
        );
      }
    },
    {
      key: 'action_type',
      label: 'Action Type',
      sortable: true,
      width: 'w-40',
      render: (log) => (
        <span className="text-sm font-medium text-gray-900 capitalize">
          {log.action_type?.replace('_', ' ') || 'N/A'}
        </span>
      )
    },
    {
      key: 'target_type',
      label: 'Target',
      sortable: true,
      width: 'w-32',
      render: (log) => (
        log.target_type ? (
          <span className="text-sm text-gray-900 capitalize">
            {log.target_type}
          </span>
        ) : (
          <span className="text-sm text-gray-400">N/A</span>
        )
      )
    },
    {
      key: 'ip_address',
      label: 'IP Address',
      sortable: true,
      width: 'w-32',
      render: (log) => (
        <span className="font-mono text-sm text-gray-900">
          {log.ip_address || 'N/A'}
        </span>
      )
    },
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      width: 'w-32',
      render: (log) => (
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900">
              {formatDate(log.created_at, 'medium')}
            </div>
            <div className="text-xs text-gray-500">
              {formatTime(log.created_at)}
            </div>
          </div>
        </div>
      )
    }
  ];

  // Define table actions
  const actions = [
    {
      label: 'View Details',
      icon: Eye,
      variant: 'default',
      onClick: (log) => {
        setSelectedLog(log);
        setShowModal(true);
      }
    }
  ];

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedLog(null);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
        <div className="bg-white border rounded-lg p-6">
          <div className="text-center">
            <Activity className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading System Logs</h3>
            <p className="text-gray-600">
              {error?.response?.data?.message || 'Failed to load system logs. Please try again later.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-4">
        {/* Header */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Activity className="w-6 h-6" />
                System Logs
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                View all system activities and admin actions
              </p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Logs</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total_logs || 0}</p>
              </div>
              <Database className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Admin Actions (24h)</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">{stats.admin_actions_24h || 0}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">User Activities (24h)</p>
                <p className="text-2xl font-bold text-purple-600 mt-1">{stats.user_activities_24h || 0}</p>
              </div>
              <Activity className="w-8 h-8 text-purple-500" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <SearchSelect
              label="Log Type"
              value={filters.log_type}
              onChange={(value) => handleFilterChange('log_type', value)}
              options={logTypeOptions}
            />
            <DatePicker
              label="From Date"
              value={filters.date_from}
              onChange={(value) => handleFilterChange('date_from', value)}
            />
            <DatePicker
              label="To Date"
              value={filters.date_to}
              onChange={(value) => handleFilterChange('date_to', value)}
            />
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <DataTable
            data={systemLogs}
            columns={columns}
            actions={actions}
            isLoading={isLoading}
            searchPlaceholder="Search by user, action type, or IP address..."
            onSearch={handleSearch}
            emptyMessage="No system logs found"
          />
        </div>
      </div>

      {/* Log Details Modal */}
      <SystemLogDetailsModal
        isOpen={showModal}
        onClose={handleCloseModal}
        log={selectedLog}
        getLogTypeColor={getLogTypeColor}
      />
    </div>
  );
};

export default AdminSystemLogs;

